/**
 * GeoDefaultsDisclosure — informational notice about region-based consent defaults.
 *
 * Shown on the Privacy dashboard page (PrivacyPage) when the user has not yet
 * made an explicit consent choice (consentedAt is null/undefined). The notice
 * explains that initial privacy defaults were set based on the user's detected
 * region, and that the user can change any setting at any time.
 *
 * If the API returns a `region` value it is shown inline so the user knows
 * which region was detected ("Defaults applied for your region (US)").  If
 * region is absent the copy falls back to a generic sentence that still
 * communicates the same principle without surfacing a potentially surprising
 * data point.
 *
 * Spec refs: spec §10, §10.7; docs/design/geo-privacy.md (slice 5).
 * WCAG: role="note" landmark, rendered as a MUI Alert with severity="info"
 * so the text is exposed to screen-readers as informational content.
 */

import type { ReactElement } from 'react';
import { Alert, AlertTitle } from '@mui/material';

export interface GeoDefaultsDisclosureProps {
  /**
   * ISO 3166 country/subdivision code stored on the consent row at signup,
   * or null/undefined when the region could not be resolved.
   */
  region?: string | null;
  /**
   * Whether the user has yet to make an explicit consent choice.
   * When false (user has customized) the notice is not rendered.
   */
  isAtDefaults: boolean;
}

/**
 * Returns the region display label if one is available, or null.
 * Exported for unit-testing the copy logic independently.
 */
export function formatRegionLabel(region: string | null | undefined): string | null {
  if (!region || typeof region !== 'string') return null;
  const code = region.trim().toUpperCase();
  if (code.length === 0) return null;
  return code;
}

export function GeoDefaultsDisclosure({
  region,
  isAtDefaults,
}: GeoDefaultsDisclosureProps): ReactElement | null {
  if (!isAtDefaults) return null;

  const regionLabel = formatRegionLabel(region);

  const regionSentence = regionLabel
    ? `Defaults applied for your region (${regionLabel}).`
    : 'Defaults were set based on your location at sign-up.';

  return (
    <Alert
      severity="info"
      role="note"
      aria-label="Region-based privacy defaults notice"
      sx={{ mb: 3 }}
    >
      <AlertTitle>Your privacy defaults were set based on your region</AlertTitle>
      {regionSentence} You have not customized these settings yet — every choice here is yours to
      make. You can change any setting at any time and your explicit choices will always override
      the regional defaults.
    </Alert>
  );
}

export default GeoDefaultsDisclosure;
